import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Bell, X, Search, Loader2, Send, Filter, Eye } from "lucide-react";

/**
 * Reusable bulk-notify modal (Sprint 73 quater).
 *
 * Props:
 *  - open        : boolean
 *  - onClose     : () => void
 *  - entityType  : 'event' | 'formation' | 'shared_service' | 'club_offer'
 *  - entityIds   : string[]  (ids of selected items)
 *  - endpoint    : string    (e.g. '/admin/events/notify-contacts-bulk')
 *  - payloadKey  : string    (e.g. 'event_ids' | 'formation_ids' | 'service_ids' | 'offer_ids')
 *  - title       : string    (modal title)
 *  - subtitle    : string    (small hint shown under title)
 */
const TYPE_LABELS = {
  event: { single: "événement", plural: "événements" },
  formation: { single: "formation", plural: "formations" },
  shared_service: { single: "service mutualisé", plural: "services mutualisés" },
  club_offer: { single: "offre Le Club", plural: "offres Le Club" },
};

export default function NotifyContactsModal({ open, onClose, entityType, entityIds, endpoint, payloadKey, title, subtitle }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [poles, setPoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selPoles, setSelPoles] = useState(new Set());
  const [selCategories, setSelCategories] = useState(new Set());
  const [query, setQuery] = useState("");
  const [selContacts, setSelContacts] = useState(new Set());
  const [extraEmails, setExtraEmails] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const labels = TYPE_LABELS[entityType] || TYPE_LABELS.event;
  const count = (entityIds || []).length;
  const ctxLabel = `${count} ${count > 1 ? labels.plural : labels.single}`;

  useEffect(() => {
    if (!open) return;
    setSelContacts(new Set());
    setSelPoles(new Set());
    setSelCategories(new Set());
    setQuery("");
    setExtraEmails("");
    setMessage("");
    setLoading(true);
    Promise.all([
      api.get("/admin/contacts?per_page=200"),
      api.get("/public/poles").catch(() => ({ data: [] })),
      api.get("/categories").catch(() => ({ data: [] })),
    ])
      .then(([rContacts, rPoles, rCats]) => {
        setContacts(rContacts.data?.items || rContacts.data || []);
        setPoles(rPoles.data || []);
        setCategories(rCats.data || []);
      })
      .catch(() => toast.error("Impossible de charger les contacts"))
      .finally(() => setLoading(false));
  }, [open]);

  const togglePole = (p) => { const ns = new Set(selPoles); if (ns.has(p)) ns.delete(p); else ns.add(p); setSelPoles(ns); };
  const toggleCat = (c) => { const ns = new Set(selCategories); if (ns.has(c)) ns.delete(c); else ns.add(c); setSelCategories(ns); };
  const toggleContact = (id) => { const ns = new Set(selContacts); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelContacts(ns); };

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return contacts.filter((c) => {
      if (q && !["email", "first_name", "last_name", "company_name"].some((k) => (c[k] || "").toLowerCase().includes(q))) return false;
      if (selPoles.size > 0 && !selPoles.has(c.company_pole || c.pole)) return false;
      if (selCategories.size > 0 && !selCategories.has(c.company_category)) return false;
      return true;
    });
  }, [contacts, query, selPoles, selCategories]);

  const selectAllFiltered = () => setSelContacts(new Set([...selContacts, ...filtered.map((c) => c.id)]));
  const clearAllSelection = () => setSelContacts(new Set());

  // Preview is only wired up for the "event" entity type for now — that's where
  // we invested in the branded template (hero + CTA). Formations / services /
  // offres continue to use the plain `_layout` so no preview is offered.
  const previewSupported = entityType === "event";
  const openPreview = async () => {
    if (!previewSupported) return;
    try {
      const r = await api.post(
        "/admin/events/notify-preview",
        { event_ids: entityIds, message: message || undefined },
        { responseType: "text", headers: { Accept: "text/html" } }
      );
      const html = typeof r.data === "string" ? r.data : "";
      if (!html) throw new Error("empty");
      const w = window.open("", "_blank", "noopener,noreferrer,width=760,height=900");
      if (!w) {
        toast.error("Ouverture bloquée — autorisez les popups pour prévisualiser.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Impossible de générer la prévisualisation");
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const extras = extraEmails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (selContacts.size === 0 && extras.length === 0) {
      toast.error("Sélectionnez au moins un contact ou un email");
      return;
    }
    setSending(true);
    try {
      const body = {
        [payloadKey]: entityIds,
        contact_ids: Array.from(selContacts),
        emails: extras,
        message,
      };
      const r = await api.post(endpoint, body);
      const total = r.data?.sent || (selContacts.size + extras.length);
      toast.success(`📣 ${total} email${total > 1 ? "s" : ""} envoyé${total > 1 ? "s" : ""} pour ${ctxLabel}`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <form onSubmit={send} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="notify-contacts-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-xl font-bold text-navy"><Bell className="h-5 w-5 text-wine-500" />{title || "Notifier des contacts"}</h3>
            <p className="mt-1 text-xs text-neutral-500">{subtitle || `Promouvoir ${ctxLabel} auprès de contacts CRM choisis ou par emails libres.`}</p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
        </div>

        {/* Filters */}
        <div className="mt-5 rounded-xl border border-wine-100 bg-wine-50/30 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-wine-700"><Filter className="mr-1 inline h-3 w-3" />Filtrer les contacts</div>

          <div className="mt-2">
            <div className="text-[10px] font-bold uppercase text-neutral-500">Pôle sectoriel</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {poles.map((p) => {
                const id = p.id || p._id || p.name;
                const active = selPoles.has(id);
                return (
                  <button key={id} type="button" onClick={() => togglePole(id)} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${active ? "bg-wine-600 text-white" : "bg-white border border-wine-200 text-wine-700 hover:bg-wine-50"}`} data-testid={`notify-filter-pole-${id}`}>
                    {p.icon ? `${p.icon} ` : ""}{p.name}
                  </button>
                );
              })}
              {poles.length === 0 && <span className="text-[11px] text-neutral-400">Aucun pôle configuré</span>}
            </div>
          </div>

          <div className="mt-3">
            <div className="text-[10px] font-bold uppercase text-neutral-500">Catégorie d&apos;adhésion</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {categories.map((c) => {
                const id = c.id || c._id || c.name;
                const active = selCategories.has(id);
                return (
                  <button key={id} type="button" onClick={() => toggleCat(id)} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${active ? "bg-wine-600 text-white" : "bg-white border border-wine-200 text-wine-700 hover:bg-wine-50"}`} data-testid={`notify-filter-cat-${id}`}>
                    {c.icon ? `${c.icon} ` : ""}{c.name}
                  </button>
                );
              })}
              {categories.length === 0 && <span className="text-[11px] text-neutral-400">Aucune catégorie configurée</span>}
            </div>
          </div>
        </div>

        <div className="mt-4 relative">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-neutral-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un contact (nom, email, société)…" className="w-full rounded-xl border border-neutral-200 pl-8 pr-3 py-2 text-sm" data-testid="notify-search" />
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-neutral-600">
          <span><strong>{filtered.length}</strong> contact{filtered.length > 1 ? "s" : ""} correspond{filtered.length > 1 ? "ent" : ""} aux filtres · <strong>{selContacts.size}</strong> sélectionné{selContacts.size > 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <button type="button" onClick={selectAllFiltered} className="rounded-full bg-wine-50 px-3 py-1 font-semibold text-wine-700 hover:bg-wine-100" data-testid="notify-select-filtered">+ Sélectionner les {filtered.length} filtrés</button>
            {selContacts.size > 0 && <button type="button" onClick={clearAllSelection} className="rounded-full bg-neutral-100 px-3 py-1 font-semibold text-neutral-700 hover:bg-neutral-200">Vider</button>}
          </div>
        </div>

        <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-neutral-100">
          {loading ? (
            <div className="p-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-wine-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-neutral-500">Aucun contact ne correspond aux filtres.</div>
          ) : filtered.slice(0, 100).map((c) => (
            <label key={c.id} className="flex items-center gap-2 border-b border-neutral-100 px-3 py-1.5 text-xs hover:bg-wine-50/30 cursor-pointer">
              <input type="checkbox" checked={selContacts.has(c.id)} onChange={() => toggleContact(c.id)} className="h-3.5 w-3.5" data-testid={`notify-contact-${c.id}`} />
              <span className="flex-1 truncate"><strong>{c.first_name || ""} {c.last_name || ""}</strong> <span className="text-neutral-500">— {c.email}</span> {c.company_name && <em className="text-neutral-400">@ {c.company_name}</em>}</span>
            </label>
          ))}
        </div>

        <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">+ Emails libres (séparés par , ou ;)</label>
        <input value={extraEmails} onChange={(e) => setExtraEmails(e.target.value)} placeholder="alice@example.com, bob@example.com" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="notify-extra-emails" />

        <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Message d&apos;intro (optionnel)</label>
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Texte d'introduction personnalisé…" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="notify-message" />

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full border border-neutral-300 px-4 py-2 text-sm" data-testid="notify-cancel">Annuler</button>
          {previewSupported && (
            <button
              type="button"
              onClick={async () => { setPreviewing(true); try { await openPreview(); } finally { setPreviewing(false); } }}
              disabled={previewing || (entityIds || []).length === 0}
              className="inline-flex items-center gap-2 rounded-full border border-wine-300 bg-white px-4 py-2 text-sm font-semibold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
              data-testid="notify-preview"
              title="Ouvre un aperçu HTML du mail dans un nouvel onglet"
            >
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Prévisualiser
            </button>
          )}
          <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="notify-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer la notification
          </button>
        </div>
      </form>
    </div>
  );
}
